/**
 * GP-482 — every app-wide keyboard shortcut, in one place.
 *
 * One source of truth (CLAUDE.md): the hook that binds the keys, the hints
 * rendered on the ribbon and down the sidebar, and the `Shift + ?` help sheet
 * all read this file. Adding a shortcut here makes it work AND makes it
 * findable — a hotkey nobody can discover is a hotkey nobody uses.
 *
 * The scheme:
 *   Shift + letter → go somewhere (the ribbon buttons + the queues down the side)
 *   Shift + digit  → switch tab WITHIN the screen you're on (Billing Queue)
 *   Enter          → Next, on every Process Billing step
 *
 * Letters and digits never overlap, so "jump to a queue" keeps working while a
 * tabbed queue is open. Nothing is bound to a bare key: a bare letter would
 * fire while staff type a patient name, and Ctrl/Alt combos collide with the
 * browser. Shift + <key> is the one space that is both free and reachable
 * one-handed.
 */

export type ShortcutGroup =
  | 'Go to a queue'
  | 'Open a panel'
  | 'Billing queue tabs'
  | 'Process billing';

export interface NavShortcut {
  /** Bare key, lower-case — matched against the normalized keydown. */
  key: string;
  /** Destination label, as it reads in the sidebar. */
  label: string;
  /**
   * Route pushed when the shortcut fires. PRX-138: the shortcut is live only
   * while this route is in the user's visible sidebar (`useVisibleMenuItems`),
   * so every sidebar rule (permission, role, module, PRX-54 visibility key,
   * nurse scope) gates the key too. No gate is copied here.
   */
  path: string;
}

export interface PanelShortcut {
  key: string;
  label: string;
}

/**
 * Ribbon buttons + the Main Operations queues down the sidebar. Mnemonic where
 * the name allows: (C)ommand centre, (B)illing, (P)ackaging, (R)eady to ship,
 * (A)ll orders; the two "Verification" screens split into cLinical-adjacent
 * `V` and prod(U)ct. PRX-138 added the rest of the operations strip: e(N)try
 * queue, subscription (M)anagement (the wireframe's Auto Refills) and
 * s(H)ipping (S is Find Patient). Ready to Ship / Staging and Shipping are
 * never live together: the PRX-16 shipping module swaps one set for the other.
 * Order here is the order of the help dialog's rows (the sidebar's order).
 */
/** The Billing Queue route; the help dialog shows the tab and wizard keys only when it is visible. */
export const BILLING_QUEUE_PATH = '/order-fulfillment/billing-queue';

export const NAV_SHORTCUTS: NavShortcut[] = [
  { key: 'c', label: 'Operations Command Center', path: '/order-fulfillment/operations-dashboard' },
  { key: 'e', label: 'PHOX Entry', path: '/order-fulfillment/Phox-entry' },
  { key: 'n', label: 'PHOX Entry queue', path: '/order-fulfillment/phox-entry-queue' },
  { key: 'b', label: 'Billing Queue', path: BILLING_QUEUE_PATH },
  { key: 'd', label: 'Scheduling Queue', path: '/order-fulfillment/schedule-delivery' },
  { key: 'v', label: 'Clinical Verification', path: '/order-fulfillment/clinical-verification' },
  { key: 'p', label: 'Packaging', path: '/order-fulfillment/packaging' },
  { key: 'u', label: 'Product Verification', path: '/order-fulfillment/product-verification' },
  { key: 'r', label: 'Ready to Ship', path: '/order-fulfillment/ready-to-ship' },
  { key: 'g', label: 'Staging', path: '/order-fulfillment/staging-area' },
  { key: 'h', label: 'Shipping', path: '/order-fulfillment/shipping' },
  { key: 'y', label: 'Delivery Status', path: '/order-fulfillment/delivery-status' },
  { key: 'a', label: 'All Orders', path: '/order-fulfillment/all-orders' },
  { key: 'm', label: 'Subscription Management', path: '/order-fulfillment/subscription-management' },
];

/** Overlays that open on top of whatever you are working on — no navigation. */
export const FIND_PATIENT_SHORTCUT: PanelShortcut = { key: 's', label: 'Find Patient' };
export const TASK_QUEUE_SHORTCUT: PanelShortcut = { key: 't', label: 'Task Queue' };
export const SEARCH_RX_SHORTCUT: PanelShortcut = { key: 'x', label: 'Search Rx' };
export const ADD_NOTE_SHORTCUT: PanelShortcut = { key: 'q', label: 'Add a note to the selected row' };
export const HELP_SHORTCUT: PanelShortcut = { key: '?', label: 'Show these keyboard shortcuts' };

export const PANEL_SHORTCUTS: PanelShortcut[] = [
  FIND_PATIENT_SHORTCUT,
  TASK_QUEUE_SHORTCUT,
  SEARCH_RX_SHORTCUT,
  ADD_NOTE_SHORTCUT,
  HELP_SHORTCUT,
];

export type BillingTabId =
  | 'billing_required'
  | 'payment_required'
  | 'edit_order'
  | 'edit_subscription'
  | 'to_be_cancelled'
  | 'iv_billing';

export interface BillingTabShortcut {
  /** Digit key, in the tab's left-to-right order. */
  key: string;
  label: string;
  tab: BillingTabId;
}

/** Shift + 1..6 across the Billing Queue tab strip, left to right. */
export const BILLING_TAB_SHORTCUTS: BillingTabShortcut[] = [
  { key: '1', label: 'To be Billed', tab: 'billing_required' },
  { key: '2', label: 'Billed - Awaiting Payment', tab: 'payment_required' },
  { key: '3', label: 'Order Troubleshooting', tab: 'edit_order' },
  { key: '4', label: 'Billing Item Troubleshooting', tab: 'edit_subscription' },
  { key: '5', label: 'Billed - To Be Cancelled', tab: 'to_be_cancelled' },
  { key: '6', label: 'To Be Billed - IV', tab: 'iv_billing' },
];

/**
 * The Process Billing wizard rides on plain browser keys rather than bound
 * hotkeys, so this list is documentation for the help sheet and the hint strip
 * under the wizard's Next button. Enter is wired in `ProcessBillingModal`;
 * Tab / Shift+Tab / Space are the native controls the steps are built to keep
 * working (every medication row is reachable by Tab and ticked with Space).
 */
export const WIZARD_KEY_HINTS: Array<{ combo: string; label: string }> = [
  { combo: 'Enter', label: 'Next step' },
  { combo: 'Tab / Shift + Tab', label: 'Move between fields' },
  { combo: 'Space', label: 'Tick the focused medication' },
];

/** "Shift + B" — the one place a combo gets turned into user-facing text. */
export function formatShortcut(key: string): string {
  return `Shift + ${key.toUpperCase()}`;
}

/** Sidebar/ribbon hint for a route, or `undefined` when it has no shortcut. */
export function shortcutForPath(path: string): string | undefined {
  const match = NAV_SHORTCUTS.find((shortcut) => shortcut.path === path);
  return match ? formatShortcut(match.key) : undefined;
}
